/**
 * useContactCampaignHistory - Histórico de disparos de campanha de um contato/cliente
 *
 * Leitura DERIVADA de `campaign_recipients` (nada é gravado durante o disparo).
 * A query só é executada sob demanda (aba "Campanhas" ativa), evitando qualquer
 * custo extra ao abrir o modal do cliente.
 *
 * Paginação keyset por (sent_at, id) DESC — suportada pelo índice parcial
 * idx_campaign_recipients_contact_sent.
 *
 * @version 1.2.0
 * RELEASE: 2026-08-09 v1.2.0 - Página de 10 itens (scroll infinito) + campos inbox_id/inbox_name
 * RELEASE: 2026-08-09 v1.1.0 - Conteúdo real da mensagem enviada
 * RELEASE: 2026-08-09 v1.0.0 - Criação inicial (RPC get_contact_campaign_history)
 */
import { useInfiniteQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';

// Logger condicional centralizado
const logger = createLogger('ContactCampaignHistory');

// Quantidade de itens por página (v1.2.0: scroll infinito de 10 em 10)
const PAGE_SIZE = 10;

/** Item de disparo retornado pela RPC */
export interface ContactCampaignHistoryItem {
  id: string;
  campaign_id: string;
  campaign_name: string | null;
  channel_type: string | null;
  message_preview: string | null;
  status: string | null;
  sent_at: string;
  delivered_at: string | null;
  read_at: string | null;
  error_message: string | null;
  /** v1.1.0: conteúdo real da mensagem enviada (join em messages) */
  message_type: string | null;
  message_content: string | null;
  message_metadata: Record<string, any> | null;
  media_url: string | null;
  /** true quando não há mensagem vinculada (usa conteúdo previsto da campanha) */
  is_fallback: boolean | null;
  /** v1.2.0: caixa de entrada usada no disparo */
  inbox_id: string | null;
  inbox_name: string | null;
}

/** Cursor keyset da paginação */
interface Cursor {
  sent_at: string;
  id: string;
}

interface Options {
  /** Só busca quando a aba estiver ativa (lazy) */
  enabled?: boolean;
}

/**
 * Busca paginada dos disparos de campanha para o cliente OU contato informado.
 *
 * @param params.customerId - ID do cliente (resolve o contato principal)
 * @param params.contactId - ID do contato (usado quando não há cliente)
 */
export function useContactCampaignHistory(
  params: { customerId?: string | null; contactId?: string | null },
  options?: Options
) {
  const { currentTenant } = useAuth();
  const { customerId, contactId } = params;

  const isEnabled =
    (options?.enabled ?? true) && !!currentTenant?.id && (!!customerId || !!contactId);

  return useInfiniteQuery({
    queryKey: ['contact-campaign-history', customerId ?? null, contactId ?? null],
    initialPageParam: null as Cursor | null,
    queryFn: async ({ pageParam }) => {
      logger.debug('Buscando histórico de campanhas', { customerId, contactId });

      const { data, error } = await (supabase.rpc as any)('get_contact_campaign_history', {
        p_customer_id: customerId ?? null,
        p_contact_id: customerId ? null : contactId ?? null,
        p_limit: PAGE_SIZE,
        p_cursor_sent_at: pageParam?.sent_at ?? null,
        p_cursor_id: pageParam?.id ?? null,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) {
        logger.error('Erro ao buscar histórico de campanhas:', error.message);
        throw error;
      }

      return (data ?? []) as ContactCampaignHistoryItem[];
    },
    // Próxima página só existe quando a atual veio cheia
    getNextPageParam: (lastPage) => {
      if (!lastPage || lastPage.length < PAGE_SIZE) return undefined;
      const last = lastPage[lastPage.length - 1];
      return { sent_at: last.sent_at, id: last.id } as Cursor;
    },
    enabled: isEnabled,
    staleTime: 60 * 1000,
  });
}
